// src/components/PaymentSection.js (With Company Name in Dropdown)

import React, { useState, useEffect, useCallback } from 'react';
import { useFirebase } from '../App';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, updateDoc, addDoc, Timestamp, deleteDoc, limit, startAfter, endBefore, limitToLast } from 'firebase/firestore';
import { toast } from 'react-toastify';
import ConfirmationModal from './Common/ConfirmationModal';
import { TableEmptyRow, TableLoadingRow } from './Common/PanelState';
import * as Sentry from "@sentry/react";

function PaymentSection() {
    const { db, user } = useFirebase();
    const [clients, setClients] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [payments, setPayments] = useState([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClientId, setSelectedClientId] = useState('');
    const [selectedCampaignId, setSelectedCampaignId] = useState('');
    const [amount, setAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [deletingPayment, setDeletingPayment] = useState(null);
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState(true);

    const [pageSize, setPageSize] = useState(15);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPayments, setTotalPayments] = useState(0);
    const [lastVisible, setLastVisible] = useState(null);
    const [firstVisible, setFirstVisible] = useState(null);
    
    useEffect(()=>{
        if(!db) return;
        const clientsUnsub = onSnapshot(query(collection(db, `clients`)), snap => setClients(snap.docs.map(d=>({id: d.id, ...d.data()}))));
        const campaignsUnsub = onSnapshot(query(collection(db, `salesOrders`)), snap => setCampaigns(snap.docs.map(d=>({id: d.id, ...d.data()}))));
        return () => { clientsUnsub(); campaignsUnsub(); };
    }, [db]);
    
    const fetchPayments = useCallback(async (direction = 'first') => {
        if (!db) { setPayments([]); setTotalPayments(0); setIsLoading(false); return; }
        setIsLoading(true);
        const startOfMonth = Timestamp.fromDate(new Date(filterYear, filterMonth - 1, 1));
        const endOfMonth = Timestamp.fromDate(new Date(filterYear, filterMonth, 0, 23, 59, 59));
        const baseQuery = query(collection(db, `payments`), where('date', '>=', startOfMonth), where('date', '<=', endOfMonth));
        if (direction === 'first') {
            const countSnapshot = await getDocs(baseQuery);
            setTotalPayments(countSnapshot.size);
        }
        let finalQuery;
        if (direction === 'next' && lastVisible) finalQuery = query(baseQuery, orderBy('date', 'desc'), startAfter(lastVisible), limit(pageSize));
        else if (direction === 'prev' && firstVisible) finalQuery = query(baseQuery, orderBy('date', 'desc'), endBefore(firstVisible), limitToLast(pageSize));
        else finalQuery = query(baseQuery, orderBy('date', 'desc'), limit(pageSize));
        try {
            const docSnap = await getDocs(finalQuery);
            setPayments(docSnap.docs.map(d => ({ id: d.id, ...d.data() })));
            if (!docSnap.empty) {
                setFirstVisible(docSnap.docs[0]);
                setLastVisible(docSnap.docs[docSnap.docs.length - 1]);
            } else { setFirstVisible(null); setLastVisible(null); }
        } catch (err) { toast.error("Error fetching payments."); } 
        finally { setIsLoading(false); }
    }, [db, filterYear, filterMonth, pageSize, lastVisible, firstVisible]);

    useEffect(() => {
        setCurrentPage(1); setLastVisible(null); setFirstVisible(null); fetchPayments('first');
    // eslint-disable-next-line
    }, [filterYear, filterMonth, pageSize]);

    useEffect(()=>{
        if(selectedClientId){ setFilteredCampaigns(campaigns.filter(c => c.clientId === selectedClientId)); } 
        else { setFilteredCampaigns([]); }
        setSelectedCampaignId('');
    }, [selectedClientId, campaigns]);

    const handleEditClick = (payment) => {
        setEditingPayment(payment);
        setSearchTerm(payment.clientName);
        setSelectedClientId(payment.clientId);
        setTimeout(() => setSelectedCampaignId(payment.saleId), 0);
        setAmount(payment.amount);
        setPaymentMethod(payment.paymentMethod);
        window.scrollTo(0, 0);
    };

    const resetForm = () => {
        setEditingPayment(null); setAmount(''); setSelectedCampaignId('');
        setSelectedClientId(''); setSearchTerm(''); setPaymentMethod('Cash');
    };

    const updateInvoiceTotal = async (campaign) => {
        if (!campaign) return;
        const paymentsQuery = query(collection(db, `payments`), where('saleId', '==', campaign.id));
        const paymentsSnapshot = await getDocs(paymentsQuery);
        const totalPaidForCampaign = paymentsSnapshot.docs.reduce((sum, p) => sum + p.data().amount, 0);
        const pendingAmountForCampaign = (campaign.totalAmount || 0) - totalPaidForCampaign;
        let newStatus = 'Unpaid';
        if (pendingAmountForCampaign <= 0) newStatus = 'Paid';
        else if (totalPaidForCampaign > 0) newStatus = 'Partially Paid';
        const invoiceQuery = query(collection(db, `invoices`), where('saleId', '==', campaign.id), limit(1));
        const invoiceSnapshot = await getDocs(invoiceQuery);
        if (!invoiceSnapshot.empty) {
            const invoiceDocRef = invoiceSnapshot.docs[0].ref;
            const updateData = { status: newStatus, totalPaid: totalPaidForCampaign, pendingAmount: pendingAmountForCampaign };
            await updateDoc(invoiceDocRef, updateData);
        }
    };
    
    const handleDeletePayment = async () => {
        if (!deletingPayment) return;
        try {
            await deleteDoc(doc(db, `payments`, deletingPayment.id));
            toast.success("Payment deleted!");
            const campaign = campaigns.find(c => c.id === deletingPayment.saleId);
            if (campaign) { await updateInvoiceTotal(campaign); }
            fetchPayments('first');
        } catch (err) { toast.error("Failed to delete payment."); } 
        finally { setDeletingPayment(null); }
    };

    const handleAddOrUpdatePayment = async(e) => {
        e.preventDefault();
        if(!selectedCampaignId || !amount) { toast.error("Please fill all fields."); return; }
        setIsSubmitting(true);
        const campaign = campaigns.find(c => c.id === selectedCampaignId);
        const client = clients.find(c => c.id === selectedClientId);
        if(!campaign || !client) { toast.error("Client or campaign details not found."); setIsSubmitting(false); return; }
        const paymentData = {
            clientId: selectedClientId, clientName: client.name, clientCompany: client.companyName, // Added companyName
            saleId: selectedCampaignId, campaignName: campaign.title, amount: parseFloat(amount), 
            paymentMethod: paymentMethod, updatedAt: Timestamp.now()
        };
        try {
            if (editingPayment) {
                await updateDoc(doc(db, `payments`, editingPayment.id), paymentData);
                toast.success("Payment updated!");
            } else {
                paymentData.date = Timestamp.now();
                await addDoc(collection(db, `payments`), paymentData);
                toast.success('Payment added!');
            }
            await updateInvoiceTotal(campaign);
            resetForm();
            fetchPayments('first');
        } catch (err) { toast.error(`Failed to process payment: ${err.message}`); } 
        finally { setIsSubmitting(false); }
    };
    
    const handleNextPage = () => { if (lastVisible) { setCurrentPage(p => p + 1); fetchPayments('next'); } };
    const handlePrevPage = () => { if (currentPage > 1) { setCurrentPage(p => p - 1); fetchPayments('prev'); } };
    const filteredClients = clients.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.companyName.toLowerCase().includes(searchTerm.toLowerCase()));
    const pageStart = totalPayments > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const pageEnd = Math.min(currentPage * pageSize, totalPayments);

    return(
         <div className="container mx-auto">
            <ConfirmationModal isOpen={!!deletingPayment} onClose={() => setDeletingPayment(null)} onConfirm={handleDeletePayment} title="Delete Payment">Are you sure you want to delete this payment record? This cannot be undone.</ConfirmationModal>
            <div className="mb-8"><h1 className="admin-page-title">Record Payments</h1><p className="admin-page-sub">Add or edit payments received from Ad Clients.</p></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1 bg-white p-8 rounded-xl shadow-md">
                    <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">{editingPayment ? 'Edit Payment' : 'New Payment Entry'}</h2>
                    <form onSubmit={handleAddOrUpdatePayment} className="space-y-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">1. Search & Select Client</label>
                            <input type="text" placeholder="Start typing client or company..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" />
                            <select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} className="mt-2 w-full p-3 bg-gray-50 rounded-lg border" required>
                                <option value="">Select a Client</option>
                                {/* --- THIS IS THE CORRECTED DROPDOWN --- */}
                                {filteredClients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.companyName})</option>)}
                            </select>
                        </div>
                        {selectedClientId && (<div><label className="block text-sm font-medium text-gray-700">2. Select Sale Order</label><select value={selectedCampaignId} onChange={e => setSelectedCampaignId(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required><option value="">Select an Order</option>{filteredCampaigns.map(c => (<option key={c.id} value={c.id}>{c.title} ({c.startDate.toDate().toLocaleString('default', { month: 'short' })})</option>))}</select></div>)}
                        <div><label className="block text-sm font-medium text-gray-700">3. Enter Amount</label><input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="PKR" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" required /></div>
                        <div><label className="block text-sm font-medium text-gray-700">4. Payment Method</label><select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border"><option>Cash</option><option>Cheque</option><option>Bank Transfer</option></select></div>
                        <div className="flex gap-2 pt-4 border-t"><button type="submit" disabled={isSubmitting} className="w-full text-white font-bold py-3 px-4 rounded-lg shadow-lg transition-transform hover:scale-105" style={{backgroundColor: isSubmitting ? '#a11217' : '#c4151c'}}>{isSubmitting ? 'Saving...' : (editingPayment ? 'Update Payment' : 'Record Payment')}</button>{editingPayment && (<button type="button" onClick={resetForm} className="w-1/3 bg-gray-200 text-gray-800 font-bold py-3 rounded-lg">Cancel</button>)}</div>
                    </form>
                </div>
                <div className="lg:col-span-2 bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="p-6 border-b flex flex-wrap justify-between items-center gap-4">
                        <h2 className="text-2xl font-bold text-gray-800">Payment History</h2>
                        <div className="flex items-center gap-2"><select value={filterMonth} onChange={e => setFilterMonth(Number(e.target.value))} className="p-2 border rounded-lg bg-gray-50">{Array.from({ length: 12 }, (_, i) => (<option key={i+1} value={i+1}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>))}</select><select value={filterYear} onChange={e => setFilterYear(Number(e.target.value))} className="p-2 border rounded-lg bg-gray-50">{Array.from({ length: 5 }, (_, i) => (<option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>))}</select></div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="px-6 py-3 text-left">Date</th><th className="px-6 py-3 text-left">Client & Order</th><th className="px-6 py-3 text-left hidden md:table-cell">Method</th><th className="px-6 py-3 text-right">Amount</th>{user.role === 'admin' && <th className="px-6 py-3 text-center">Actions</th>}</tr></thead>
                            <tbody className="divide-y divide-gray-200">
                                {isLoading ? (<TableLoadingRow colSpan={5} label="Loading payments" />)
                                : payments.length > 0 ? payments.map(p => (
                                    <tr key={p.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{p.date.toDate().toLocaleDateString()}</td>
                                        <td className="px-6 py-4">
                                            {/* --- THIS IS THE CORRECTED TABLE CELL --- */}
                                            <div className="font-semibold text-gray-800">{p.clientName}</div>
                                            <div className="text-xs text-gray-500">{p.clientCompany}</div>
                                            <div className="text-xs text-gray-400 mt-1">{p.campaignName}</div>
                                        </td>
                                        <td className="px-6 py-4 hidden md:table-cell text-gray-500">{p.paymentMethod}</td>
                                        <td className="px-6 py-4 text-right font-bold text-green-600">PKR {p.amount.toLocaleString()}</td>
                                        {user.role === 'admin' && (<td className="px-6 py-4 text-center whitespace-nowrap"><button onClick={() => handleEditClick(p)} className="font-semibold text-blue-600 hover:text-blue-800">Edit</button><span className="mx-2 text-gray-300">|</span><button onClick={() => setDeletingPayment(p)} className="font-semibold text-red-600 hover:text-red-800">Delete</button></td>)}
                                    </tr>
                                )) : (<TableEmptyRow colSpan={user.role === 'admin' ? 5 : 4} title="No payments found" hint="No payments recorded for this period." />)}
                            </tbody>
                        </table>
                    </div>
                    {totalPayments > pageSize && (
                        <div className="p-4 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 rounded-b-xl border-t">
                            <span className="text-sm font-medium text-gray-700">Showing {pageStart}-{pageEnd} of {totalPayments}</span>
                            <div className="flex gap-2"><button onClick={handlePrevPage} disabled={currentPage === 1 || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Previous</button><button onClick={handleNextPage} disabled={!lastVisible || pageEnd >= totalPayments || isLoading} className="px-4 py-2 text-sm font-semibold text-white bg-gray-600 rounded-lg shadow disabled:bg-gray-300">Next</button></div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PaymentSection;